import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Download, EnvelopeSimple, Translate } from "@phosphor-icons/react";

/**
 * ContractDialog — reusable dialog to generate + download/email a client or interpreter contract.
 * Usage:
 *   <ContractDialog kind="client" projectId={id}>...</ContractDialog>
 *   <ContractDialog kind="interpreter" interpreterId={id}>...</ContractDialog>
 */
export function ContractDialog({ kind, projectId, interpreterId, children, defaultAmount }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(defaultAmount || "");
  const [emailTo, setEmailTo] = useState("");
  const [note, setNote] = useState("");
  const [contract, setContract] = useState(null);
  const [language, setLanguage] = useState("en");

  const { data: languages = [] } = useQuery({
    queryKey: ["contract-languages"],
    queryFn: async () => (await api.get("/contracts/languages")).data,
    enabled: kind === "client",
  });

  // Auto-select the client's preferred agreement language when the dialog opens.
  useEffect(() => {
    if (kind !== "client" || !open || !projectId) return;
    (async () => {
      try {
        const proj = (await api.get(`/projects/${projectId}`)).data;
        if (proj?.client_id) {
          const client = (await api.get(`/clients/${proj.client_id}`)).data;
          if (client?.preferred_language) setLanguage(client.preferred_language);
        }
      } catch (e) {
        // Non-fatal: language falls back to English. Log for observability during dev.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("Could not preload client preferred language:", e?.response?.status || e?.message);
        }
      }
    })();
  }, [kind, open, projectId]);

  const generate = useMutation({
    mutationFn: async () => {
      if (kind === "client") {
        return (await api.post(`/contracts/client/${projectId}/generate`, {
          origin_url: window.location.origin,
          create_stripe_link: true,
          override_amount: amount ? parseFloat(amount) : undefined,
          language,
        })).data;
      }
      return (await api.post(`/contracts/interpreter/${interpreterId}/generate`, {})).data;
    },
    onSuccess: (data) => {
      setContract(data);
      toast.success("Contract generated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const download = async () => {
    if (!contract) return;
    const url = `${API}/contracts/${contract.contract_id}/download`;
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${contract.contract_number}.docx`;
    a.click();
  };

  const email = useMutation({
    mutationFn: async () => (await api.post(`/contracts/${contract.contract_id}/email`, {
      to: emailTo || undefined, message: note || undefined,
    })).data,
    onSuccess: (data) => toast.success(`Emailed to ${data.sent_to}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Email failed"),
  });

  const sendClientSignLink = useMutation({
    mutationFn: async () => (await api.post(`/contracts/${contract.contract_id}/send-client-signing-link`)).data,
    onSuccess: (d) => {
      toast.success("Client e-sign link emailed");
      if (d?.sign_url) navigator.clipboard?.writeText(d.sign_url)?.catch(() => {});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send signing link"),
  });

  // For the CLIENT contract flow: one-click "Send e-sign link" that auto-generates
  // (idempotent — the backend reuses any existing unsigned contract for this project)
  // WITHOUT creating a Stripe session upfront. The deposit / payment link fires only
  // AFTER the client e-signs, avoiding the "multiple pending payment links" problem.
  const sendEsign = useMutation({
    mutationFn: async () => {
      let ctr = contract;
      if (!ctr) {
        ctr = (await api.post(`/contracts/client/${projectId}/generate`, {
          origin_url: window.location.origin,
          create_stripe_link: false,   // <-- key change: no Stripe session at this stage
          override_amount: amount ? parseFloat(amount) : undefined,
          language,
        })).data;
        setContract(ctr);
      }
      return (await api.post(`/contracts/${ctr.contract_id}/send-client-signing-link`)).data;
    },
    onSuccess: (d) => {
      toast.success(d?.sent_to ? `E-sign link emailed to ${d.sent_to}` : "E-sign link sent");
      if (d?.sign_url) navigator.clipboard?.writeText(d.sign_url)?.catch(() => {});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send e-sign link"),
  });

  const reset = (o) => { setOpen(o); if (!o) { setContract(null); setAmount(defaultAmount || ""); setEmailTo(""); setNote(""); } };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <span onClick={() => setOpen(true)}>{children}</span>
      <DialogContent className="bg-white sm:max-w-md" data-testid={`contract-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={20} weight="duotone" className="text-blue-800" />
            {kind === "client" ? "Interpreter Services Agreement" : "1099 Contractor Agreement"}
          </DialogTitle>
        </DialogHeader>

        {kind === "client" ? (
          <div className="space-y-4">
            {/* Recommended flow explainer */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 leading-relaxed">
              <b>Recommended flow:</b> Send the e-sign link → client signs → deposit link goes out → confirmation email. No PDF or duplicate payment link needed upfront.
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                <Translate size={12} /> Contract language
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="mt-1" data-testid="contract-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {languages.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`contract-language-${l.code}`}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total flat fee ($USD)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="600.00" className="mt-1" data-testid="contract-amount" />
              <div className="text-[11px] text-slate-500 mt-1">Leave blank to auto-compute from project rate × hours. Client sees this on the signing page.</div>
            </div>

            {/* PRIMARY ACTION — one click */}
            <Button
              onClick={() => sendEsign.mutate()}
              disabled={sendEsign.isPending}
              className="w-full h-11 bg-red-700 hover:bg-red-800 text-white text-base"
              data-testid="contract-send-client-esign"
            >
              {sendEsign.isPending
                ? "Sending e-sign link…"
                : (contract ? "Resend e-sign link to client" : "Send e-sign link to client")}
            </Button>

            {contract && (
              <div className="text-[11px] text-emerald-700 text-center">
                Contract <b>{contract.contract_number}</b> ready. Deposit link fires when the client signs.
              </div>
            )}

            {/* Secondary actions — collapsed by default */}
            <details className="border-t border-slate-200 pt-3">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 select-none">
                Advanced: download DOCX, or email a full copy with a pay-now link (skips e-sign)
              </summary>
              <div className="mt-3 space-y-2">
                {!contract && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      const d = (await api.post(`/contracts/client/${projectId}/generate`, {
                        origin_url: window.location.origin,
                        create_stripe_link: true,
                        override_amount: amount ? parseFloat(amount) : undefined,
                        language,
                      })).data;
                      setContract(d);
                      toast.success(d.reused ? "Loaded existing contract" : "Contract + Stripe link generated");
                    }}
                    data-testid="contract-generate-full"
                  >
                    Generate DOCX + Stripe pay link
                  </Button>
                )}
                {contract && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={download} variant="outline" size="sm" data-testid="contract-download">
                      <Download size={13} className="mr-1" /> Download .docx
                    </Button>
                    <Button onClick={() => email.mutate()} disabled={email.isPending} size="sm" variant="outline" data-testid="contract-email">
                      <EnvelopeSimple size={13} className="mr-1" /> {email.isPending ? "Sending…" : "Email full DOCX"}
                    </Button>
                  </div>
                )}
                {contract && (
                  <>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Send to (optional override)</Label>
                    <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="override@example.com" />
                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to include in the email" />
                  </>
                )}
              </div>
            </details>
          </div>
        ) : !contract ? (
          <div className="space-y-4">
            <Button onClick={() => generate.mutate()} disabled={generate.isPending} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="contract-generate">
              {generate.isPending ? "Generating…" : "Generate contract"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
              <div className="font-semibold text-emerald-800">Ready: {contract.contract_number}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={download} variant="outline" data-testid="contract-download">
                <Download size={14} className="mr-1.5" /> Download .docx
              </Button>
              <Button onClick={() => email.mutate()} disabled={email.isPending} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="contract-email">
                <EnvelopeSimple size={14} className="mr-1.5" /> {email.isPending ? "Sending…" : "Email to party"}
              </Button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Send to (optional override)</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="override@example.com" className="mt-1" />
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" className="mt-2" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
